import { Platform, Share } from 'react-native';

import { showAlert } from '@/src/utils/alert';

/**
 * What actually happened, so a caller can confirm the silent paths. Only web
 * ever returns 'copied': the native share sheet is its own confirmation.
 */
export type ShareOutcome = 'shared' | 'copied' | 'failed';

/**
 * Open the platform's share sheet for a link.
 *
 * Three different mechanisms behind one call:
 *
 *   - iOS takes the link in `url`. It must NOT also go in `message` — the two
 *     fields are shared as separate items, and targets that would render a rich
 *     link preview fall back to plain text once there's a string alongside it.
 *   - Android's ShareContent has no `url` field at all, so the link IS the
 *     message. `title` is only the chooser's heading there.
 *   - Web has no share sheet we can count on: `navigator.share` needs a secure
 *     context and is missing from desktop Firefox. Copy-to-clipboard is the
 *     fallback, and 'copied' is the caller's cue to say so on screen.
 */
export async function shareLink(
  url: string,
  title?: string
): Promise<ShareOutcome> {
  if (Platform.OS === 'web') return shareLinkOnWeb(url, title);

  try {
    await Share.share(
      Platform.OS === 'ios' ? { url, title } : { message: url, title }
    );
    // A dismissed sheet resolves with `action: 'dismissedAction'` rather than
    // throwing — nothing went wrong, so it isn't reported as a failure.
    return 'shared';
  } catch {
    showAlert("Couldn't share", 'Please try again later.');
    return 'failed';
  }
}

async function shareLinkOnWeb(
  url: string,
  title?: string
): Promise<ShareOutcome> {
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, url });
      return 'shared';
    } catch (error) {
      // Dismissing the sheet rejects with AbortError — a finished interaction,
      // not a reason to fall through to the clipboard.
      if (error instanceof Error && error.name === 'AbortError')
        return 'shared';
    }
  }

  try {
    // `navigator.clipboard` is undefined outside a secure context, so that case
    // is a TypeError rather than a rejection — the same catch covers both.
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    // Last resort, and deliberately not a silent 'failed': `showAlert` renders
    // nothing on web (no AlertHost since the rehaul), so a prompt is the only
    // way left to put the link somewhere the user can actually take it from.
    window.prompt('Copy this link', url);
    return 'failed';
  }
}
